import React, { useState, useEffect } from 'react';
import { X, Eye, FileText, CheckCircle, AlertCircle, Loader2, Download } from 'lucide-react';
import { getSOPPreview, type SOPPreviewResponse } from '../../api';
import toast from 'react-hot-toast';

interface SOPPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  processMapId: number;
  processMapLevel?: number;
  projectName?: string;
}

const SOPPreviewModal: React.FC<SOPPreviewModalProps> = ({
  isOpen,
  onClose,
  processMapId,
  processMapLevel = 3,
  projectName = 'Project'
}) => {
  const [loading, setLoading] = useState(false);
  const [previewData, setPreviewData] = useState<SOPPreviewResponse | null>(null);
  const [viewMode, setViewMode] = useState<'preview' | 'diff'>('preview');
  const [includeMetadata, setIncludeMetadata] = useState(true);

  // Load preview data when modal opens
  useEffect(() => {
    if (isOpen && processMapId) {
      loadPreview();
    }
  }, [isOpen, processMapId]);

  const loadPreview = async () => {
    try {
      setLoading(true);
      const response = await getSOPPreview(processMapId.toString(), {
        include_metadata: includeMetadata,
        preview_mode: 'full'
      });

      console.log('SOP Preview loaded:', response);
      setPreviewData(response);
    } catch (error: any) {
      console.error('Failed to load SOP preview:', error);
      toast.error('Failed to load SOP preview: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!previewData) return;

    // Create downloadable content
    const content = `${previewData.preview.title}\n\n` +
      `Generated: ${new Date(previewData.preview.generated_at).toLocaleString()}\n` +
      `Source: ${previewData.preview.generated_from}\n\n` +
      previewData.preview.sections.map(section =>
        `${section.section_number}. ${section.section_title}\n\n${formatContent(section.content)}\n\n`
      ).join('---\n\n');

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sop-preview-${projectName}-level${processMapLevel}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast.success('SOP Preview downloaded!');
  };

  const getSourceTypeBadge = (sourceType: string) => {
    const badges = {
      'preserved_human_content': 'bg-blue-100 text-blue-700 border-blue-200',
      'generated_from_process_map': 'bg-green-100 text-green-700 border-green-200',
      'ai_generated': 'bg-purple-100 text-purple-700 border-purple-200',
      'template_generated': 'bg-amber-100 text-amber-700 border-amber-200',
      'manual': 'bg-gray-100 text-gray-700 border-gray-200'
    };
    return badges[sourceType as keyof typeof badges] || 'bg-gray-100 text-gray-700 border-gray-200';
  };

  const getValidationBadge = (status: string) => {
    const badges = {
      'validated': 'bg-green-100 text-green-700 border-green-200',
      'unvalidated': 'bg-gray-100 text-gray-600 border-gray-200',
      'needs_review': 'bg-orange-100 text-orange-700 border-orange-200'
    };
    return badges[status as keyof typeof badges] || 'bg-gray-100 text-gray-700 border-gray-200';
  };

  // Format content to remove escape characters and handle markdown-style formatting
  const formatContent = (content: string) => {
    if (!content) return '';

    return content
      // Replace escaped newlines with actual newlines
      .replace(/\\n\\n/g, '\n\n')
      .replace(/\\n/g, '\n')
      // Handle bold text markers
      .replace(/\*\*(.*?)\*\*/g, '$1')
      // Handle numbered lists better
      .replace(/(\n|^)(\d+\.\d+\s)/g, '\n\n$2')
      // Handle control points and gaps better
      .replace(/\[CONTROL POINT:/g, '\n[CONTROL POINT:')
      .replace(/\[GAP IDENTIFIED:/g, '\n[GAP IDENTIFIED:')
      .replace(/\[DOCUMENTED:/g, '\n[DOCUMENTED:')
      .replace(/\[ASSUMED:/g, '\n[ASSUMED:')
      .replace(/\[INFERRED:/g, '\n[INFERRED:')
      // Clean up any remaining double backslashes
      .replace(/\\\\/g, '\\')
      // Clean up extra spaces around newlines
      .replace(/\n\s+\n/g, '\n\n')
      // Trim extra whitespace
      .trim();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <Eye className="w-6 h-6 text-blue-600" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">SOP Preview</h2>
              <p className="text-sm text-gray-600">
                Preview what the SOP would look like after regeneration
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            {/* View Mode Toggle */}
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('preview')}
                className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                  viewMode === 'preview'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Preview
              </button>
              {previewData?.diff_analysis && (
                <button
                  onClick={() => setViewMode('diff')}
                  className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                    viewMode === 'diff'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Changes
                </button>
              )}
            </div>

            {/* Download Button */}
            {previewData && (
              <button
                onClick={handleDownload}
                className="flex items-center space-x-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Download className="w-4 h-4" />
                <span className="text-sm">Download</span>
              </button>
            )}

            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {loading ? (
            <div className="flex items-center justify-center flex-1">
              <div className="text-center">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-3" />
                <div className="text-gray-600 font-medium">Generating SOP Preview...</div>
                <div className="text-sm text-gray-500 mt-2">
                  Using existing SOP generation logic to create preview from your process map
                </div>
                <div className="text-xs text-gray-400 mt-2">
                  Preserving verified content and generating new sections as needed
                </div>
              </div>
            </div>
          ) : previewData ? (
            <div className="flex-1 overflow-y-auto">
              {viewMode === 'preview' ? (
                <div className="p-6 space-y-6">
                  {/* Preview Summary */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                      <div>
                        <div className="text-gray-600">Total Sections</div>
                        <div className="font-semibold text-lg">{previewData.preview.summary.total_sections}</div>
                      </div>
                      <div>
                        <div className="text-gray-600">Level</div>
                        <div className="font-semibold text-lg">{previewData.preview.summary.process_map_level}</div>
                      </div>
                      <div>
                        <div className="text-gray-600">Preserved</div>
                        <div className="font-semibold text-lg text-blue-600">
                          {previewData.preview.summary.preserved_sections}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-600">Generated</div>
                        <div className="font-semibold text-lg text-green-600">
                          {previewData.preview.summary.new_sections}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-600">Method</div>
                        <div className="font-semibold text-xs text-blue-600">
                          {previewData.preview.summary.generated_using === 'existing_sop_linkage_service' ? 'SOP Service' : 'Template'}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* SOP Title */}
                  <div className="border-b border-gray-200 pb-4">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                      {previewData.preview.title}
                    </h3>
                    <div className="flex items-center space-x-4 text-sm text-gray-600">
                      <span>Version: {previewData.preview.version}</span>
                      <span>Generated: {new Date(previewData.preview.generated_at).toLocaleString()}</span>
                      <span>Source: {previewData.preview.generated_from}</span>
                    </div>
                  </div>

                  {/* SOP Sections */}
                  <div className="space-y-6">
                    {previewData.preview.sections.map((section, index) => (
                      <div key={index} className="bg-white border border-gray-200 rounded-lg p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h4 className="text-lg font-semibold text-gray-900 mb-1">
                              {section.section_number}. {section.section_title}
                            </h4>
                            {section.metadata && includeMetadata && (
                              <div className="flex items-center space-x-2 flex-wrap gap-2">
                                {/* Source Type Badge */}
                                <span className={`px-2 py-1 rounded border text-xs font-medium ${getSourceTypeBadge(section.metadata.source_type)}`}>
                                  {section.metadata.source_type === 'preserved_human_content' && '💾 Preserved'}
                                  {section.metadata.source_type === 'generated_from_process_map' && '🔄 Generated'}
                                  {section.metadata.source_type === 'ai_generated' && '🤖 AI Generated'}
                                  {section.metadata.source_type === 'template_generated' && '📋 Template'}
                                  {section.metadata.source_type === 'manual' && '✋ Manual'}
                                </span>

                                {/* Validation Status Badge */}
                                <span className={`px-2 py-1 rounded border text-xs font-medium ${getValidationBadge(section.metadata.validation_status)}`}>
                                  {section.metadata.validation_status === 'validated' && '✅ Validated'}
                                  {section.metadata.validation_status === 'unvalidated' && '⏳ Unvalidated'}
                                  {section.metadata.validation_status === 'needs_review' && '⚠️ Needs Review'}
                                </span>

                                {/* Confidence Score */}
                                {section.metadata.ai_confidence && (
                                  <span className="px-2 py-1 bg-purple-100 text-purple-700 border border-purple-200 rounded text-xs font-medium">
                                    {Math.round(section.metadata.ai_confidence * 100)}% confidence
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Section Content */}
                        <div className="prose prose-sm max-w-none">
                          <div className="whitespace-pre-wrap text-gray-700">
                            {formatContent(section.content)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                /* Diff View */
                <div className="p-6 space-y-6 overflow-y-auto">
                  {previewData.diff_analysis ? (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">
                        Changes Compared to Existing SOP
                      </h3>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {/* Added Sections */}
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <div className="flex items-center space-x-2 mb-3">
                            <CheckCircle className="w-5 h-5 text-green-600" />
                            <h4 className="font-semibold text-green-900">
                              Added Sections ({previewData.diff_analysis.comparison_with_existing.changes.added_sections.length})
                            </h4>
                          </div>
                          {previewData.diff_analysis.comparison_with_existing.changes.added_sections.length > 0 ? (
                            <div className="space-y-2">
                              {previewData.diff_analysis.comparison_with_existing.changes.added_sections.map((section, index) => (
                                <div key={index} className="text-sm text-green-700 bg-green-100 p-2 rounded">
                                  {section.title}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-green-600">No new sections will be added</p>
                          )}
                        </div>

                        {/* Modified Sections */}
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                          <div className="flex items-center space-x-2 mb-3">
                            <AlertCircle className="w-5 h-5 text-amber-600" />
                            <h4 className="font-semibold text-amber-900">
                              Modified Sections ({previewData.diff_analysis.comparison_with_existing.changes.modified_sections.length})
                            </h4>
                          </div>
                          {previewData.diff_analysis.comparison_with_existing.changes.modified_sections.length > 0 ? (
                            <div className="space-y-2">
                              {previewData.diff_analysis.comparison_with_existing.changes.modified_sections.map((section, index) => (
                                <div key={index} className="text-sm text-amber-700 bg-amber-100 p-2 rounded">
                                  {section.title}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-amber-600">No sections will be modified</p>
                          )}
                        </div>

                        {/* Removed Sections */}
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                          <div className="flex items-center space-x-2 mb-3">
                            <X className="w-5 h-5 text-red-600" />
                            <h4 className="font-semibold text-red-900">
                              Removed Sections ({previewData.diff_analysis.comparison_with_existing.changes.removed_sections.length})
                            </h4>
                          </div>
                          {previewData.diff_analysis.comparison_with_existing.changes.removed_sections.length > 0 ? (
                            <div className="space-y-2">
                              {previewData.diff_analysis.comparison_with_existing.changes.removed_sections.map((section, index) => (
                                <div key={index} className="text-sm text-red-700 bg-red-100 p-2 rounded">
                                  {section.section_number}. {section.title}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-red-600">No sections will be removed</p>
                          )}
                        </div>
                      </div>

                      {/* Summary Stats */}
                      <div className="mt-6 bg-gray-50 rounded-lg p-4">
                        <h4 className="font-semibold text-gray-900 mb-2">Summary</h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <div className="text-gray-600">Current SOP</div>
                            <div className="font-semibold">{previewData.diff_analysis.comparison_with_existing.existing_sections_count} sections</div>
                          </div>
                          <div>
                            <div className="text-gray-600">New SOP</div>
                            <div className="font-semibold">{previewData.diff_analysis.comparison_with_existing.new_sections_count} sections</div>
                          </div>
                          <div>
                            <div className="text-gray-600">Net Change</div>
                            <div className="font-semibold">
                              {previewData.diff_analysis.comparison_with_existing.new_sections_count - previewData.diff_analysis.comparison_with_existing.existing_sections_count > 0 ? '+' : ''}
                              {previewData.diff_analysis.comparison_with_existing.new_sections_count - previewData.diff_analysis.comparison_with_existing.existing_sections_count}
                            </div>
                          </div>
                          <div>
                            <div className="text-gray-600">Impact</div>
                            <div className="font-semibold">
                              {previewData.diff_analysis.comparison_with_existing.changes.added_sections.length +
                               previewData.diff_analysis.comparison_with_existing.changes.modified_sections.length +
                               previewData.diff_analysis.comparison_with_existing.changes.removed_sections.length} changes
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-500">No existing SOP found to compare with</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center flex-1">
              <div className="text-center">
                <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">Failed to load SOP preview</p>
                <button
                  onClick={loadPreview}
                  className="mt-3 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Retry
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={includeMetadata}
                  onChange={(e) => setIncludeMetadata(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">Include metadata</span>
              </label>
              <button
                onClick={loadPreview}
                disabled={loading}
                className="text-sm text-blue-600 hover:text-blue-700 disabled:text-gray-400"
              >
                Refresh Preview
              </button>
            </div>

            <div className="flex items-center space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SOPPreviewModal;